/* eslint-disable max-lines */
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import PageHeader from '@components/layout/page-header';
import {
  RefreshCw,
  Clock,
  RotateCcw,
  Calendar,
  ChevronDown,
  Search
} from 'lucide-react';
import Button from '@components/atoms/ui-kit/button';

// Components
import SystemHealth from '@components/back-office/operations/system-health';
import LiveActivity from '@components/back-office/operations/live-activity';
import ContextualActions from '@components/back-office/operations/contextual-actions';
import SessionDetailDrawer from '@components/back-office/operations/session-detail-drawer';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal';

// Hooks
import { useGetOperationsLive } from '@utils/api/back-office/operations';
import { useGetAllStrandedSessionIds } from '@utils/api/back-office/stranded-sessions/use-get-all-stranded-session-ids';
import { useRetryStrandedSessions } from '@utils/api/back-office/stranded-sessions/use-post-retry';

// Types
import {
  OperationsSession,
  SESSION_STATUS_OPTIONS,
  TimeRangePreset
} from '@utils/types/back-office/operations';

const STATUS_CHIP_ACTIVE: Record<string, string> = {
  queued: 'bg-blue-500 text-white border-transparent',
  processing: 'bg-amber-500 text-white border-transparent',
  streaming: 'bg-sky-500 text-white border-transparent',
  finished: 'bg-emerald-500 text-white border-transparent',
  error: 'bg-red-500 text-white border-transparent',
  failed: 'bg-red-500 text-white border-transparent',
  stranded: 'bg-orange-500 text-white border-transparent'
};

export default function OperationsBo() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get filters from URL
  const statusFilter = searchParams.get('status') || undefined;
  const timeRangeParam = searchParams.get(
    'time_range'
  ) as TimeRangePreset | null;
  const dateFrom = searchParams.get('date_from') || undefined;
  const dateTo = searchParams.get('date_to') || undefined;
  const timeRange: TimeRangePreset =
    dateFrom || dateTo ? 'custom' : timeRangeParam || '24h';
  const limit = parseInt(searchParams.get('limit') || '25', 10);
  const offset = parseInt(searchParams.get('offset') || '0', 10);
  const search = searchParams.get('search') || undefined;

  // The DataGrid column filter writes URL `q` params shaped like
  // `field:op:value` (e.g. `filename:ilike:%channel1%`). Parse all
  // column-level filters from q and forward them as dedicated API params.
  const columnFilters = useMemo(() => {
    const qParams = searchParams.getAll('q');
    const result: Record<string, string> = {};
    for (const entry of qParams) {
      const colonAfterField = entry.indexOf(':');
      if (colonAfterField === -1) continue;
      const field = entry.slice(0, colonAfterField);
      const rest = entry.slice(colonAfterField + 1);
      const colonAfterOp = rest.indexOf(':');
      if (colonAfterOp === -1) continue;
      const value = rest
        .slice(colonAfterOp + 1)
        .replace(/^%/, '')
        .replace(/%$/, '');
      if (value) result[field] = value;
    }
    return result;
  }, [searchParams]);

  const filename = columnFilters['filename'];
  const sessionIdFilter = columnFilters['session_id'];
  const organizationNameFilter = columnFilters['organization_name'];
  const statusFromQ = columnFilters['status'];
  const minDuration = columnFilters['duration']
    ? parseInt(columnFilters['duration'], 10)
    : undefined;

  const effectiveStatus = statusFilter || statusFromQ;

  const sort = searchParams.get('sort') || undefined;
  const direction =
    (searchParams.get('direction') as 'asc' | 'desc' | null) || undefined;

  // Local state
  const [selectedSessionIds, setSelectedSessionIds] = useState<string[]>([]);
  // Cache stores last-known snapshot of each selected session so the panel
  // survives auto-refresh moving sessions off the current page.
  const [sessionCache, setSessionCache] = useState<
    Record<string, OperationsSession>
  >({});
  const [detailSession, setDetailSession] = useState<OperationsSession | null>(
    null
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isRetryModalOpen, setIsRetryModalOpen] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isTimeRangeOpen, setIsTimeRangeOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(search ?? '');

  // Sync local search state when URL changes externally (e.g. back button)
  useEffect(() => {
    setSearchInput(search ?? '');
  }, [search]);

  // Debounce search input into URL param
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === (search ?? '')) return;
    const timer = setTimeout(() => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (trimmed) {
          next.set('search', trimmed);
        } else {
          next.delete('search');
        }
        next.delete('offset');
        return next;
      });
      setSelectedSessionIds([]);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, search, setSearchParams]);

  // Clear row selection whenever any column filter changes.
  useEffect(() => {
    setSelectedSessionIds([]);
  }, [
    filename,
    sessionIdFilter,
    organizationNameFilter,
    statusFromQ,
    minDuration
  ]);

  // Fetch operations data
  const { data, isLoading, isFetching, refetch, dataUpdatedAt } =
    useGetOperationsLive(
      {
        status: effectiveStatus,
        search,
        filename,
        session_id: sessionIdFilter,
        organization_name: organizationNameFilter,
        min_duration: minDuration,
        sort: sort as
          | 'created_at'
          | 'date_session_start'
          | 'status'
          | 'organization'
          | 'duration'
          | 'session_id'
          | 'filename'
          | undefined,
        direction,
        time_range: timeRange === 'custom' ? undefined : timeRange,
        date_from: dateFrom,
        date_to: dateTo,
        limit,
        offset
      },
      autoRefresh ? 5000 : false
    );

  // Stranded sessions hooks
  const { data: allStrandedData, isLoading: isLoadingStrandedIds } =
    useGetAllStrandedSessionIds();
  const { mutate: retrySessions } = useRetryStrandedSessions();

  const strandedSessionIds = allStrandedData?.sessionIds ?? [];
  const totalStrandedSessions = allStrandedData?.total ?? 0;

  // Sum per-status counts so "All" chip is consistent with individual chips.
  // session_counts.total is the paginated/filtered count and disagrees with the
  // per-status totals returned by the same response.
  const allSessionsCount = useMemo(() => {
    const c = data?.session_counts;
    if (!c) return data?.total_results ?? 0;
    const sum =
      (c.processing ?? 0) +
      (c.queued ?? 0) +
      (c.uploaded ?? 0) +
      (c.error ?? 0) +
      (c.finished ?? 0) +
      (c.stranded ?? 0) +
      (c.streaming ?? 0);
    return sum > 0 ? sum : (data?.total_results ?? 0);
  }, [data]);

  // Handle retry all stranded sessions
  const handleRetryAllStranded = () => {
    if (!strandedSessionIds.length) return;
    setIsRetrying(true);
    retrySessions(strandedSessionIds, {
      onSettled: () => {
        setIsRetryModalOpen(false);
        setIsRetrying(false);
      },
      onError: (error: unknown) => {
        const err = error as {
          response?: {
            data?: {
              error_description?: string;
              detail?: string;
              message?: string;
            };
          };
        };
        const msg =
          err?.response?.data?.error_description ||
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          t('an-error-occurred');
        toast.error(msg);
      }
    });
  };

  // Get sessions from result array
  const sessions = useMemo(() => {
    return data?.result || [];
  }, [data?.result]);

  // Sessions are already filtered server-side by status
  const filteredSessions = sessions;

  // When fresh data arrives, update cached snapshots for any selected sessions
  // that are still on the current page. Snapshots for off-page sessions are
  // kept as-is so the panel survives pagination/refresh.
  useEffect(() => {
    if (!sessions.length || !selectedSessionIds.length) return;
    const updates: Record<string, OperationsSession> = {};
    for (const s of sessions) {
      if (selectedSessionIds.includes(s.session_id)) updates[s.session_id] = s;
    }
    if (Object.keys(updates).length) {
      setSessionCache(prev => ({ ...prev, ...updates }));
    }
  }, [sessions]);

  // Stable selected sessions — from cache, not from the current page data.
  const selectedSessions = useMemo(
    () =>
      selectedSessionIds
        .map(id => sessionCache[id])
        .filter((s): s is OperationsSession => !!s),
    [selectedSessionIds, sessionCache]
  );

  // Format last updated time
  const lastUpdated = useMemo(() => {
    if (!dataUpdatedAt) return '';
    const date = new Date(dataUpdatedAt);
    return date.toLocaleTimeString();
  }, [dataUpdatedAt]);

  // Handle status filter change
  const handleStatusFilter = (status: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (status) {
      newParams.set('status', status);
    } else {
      newParams.delete('status');
    }
    // Reset pagination when changing filters
    newParams.delete('offset');
    setSearchParams(newParams);
    setSelectedSessionIds([]);
  };

  // Handle time range change
  const handleTimeRangeChange = (newTimeRange: TimeRangePreset) => {
    const newParams = new URLSearchParams(searchParams);
    if (newTimeRange === 'custom') {
      // Keep dropdown open for date input; don't close yet
      return;
    }
    // Clear custom date params when switching to a preset
    newParams.delete('date_from');
    newParams.delete('date_to');
    if (newTimeRange === '24h') {
      newParams.delete('time_range');
    } else {
      newParams.set('time_range', newTimeRange);
    }
    // Reset pagination when changing filters
    newParams.delete('offset');
    setSearchParams(newParams);
    setIsTimeRangeOpen(false);
  };

  // Handle custom date range
  const handleCustomDateChange = (
    field: 'date_from' | 'date_to',
    value: string
  ) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('time_range');
    if (value) {
      newParams.set(field, value);
    } else {
      newParams.delete(field);
    }
    newParams.delete('offset');
    setSearchParams(newParams);
  };

  // Time range options
  const timeRangeOptions: { value: TimeRangePreset; label: string }[] = [
    { value: '24h', label: t('last-24-hours') },
    { value: '7d', label: t('last-7-days') },
    { value: '30d', label: t('last-30-days') },
    { value: 'all', label: t('all-time') },
    { value: 'custom', label: t('custom-range') }
  ];

  // Format the active time range label
  const activeTimeRangeLabel = useMemo(() => {
    if (timeRange === 'custom') {
      if (dateFrom && dateTo) {
        return `${dateFrom.slice(0, 10)} — ${dateTo.slice(0, 10)}`;
      }
      if (dateFrom) return `${t('from')} ${dateFrom.slice(0, 10)}`;
      if (dateTo) return `${t('until')} ${dateTo.slice(0, 10)}`;
      return t('custom-range');
    }
    return timeRangeOptions.find(opt => opt.value === timeRange)?.label ?? '';
  }, [timeRange, dateFrom, dateTo, t]);

  // Row click: single-select (replaces current selection).
  // Multi-select via checkboxes goes through handleSelectMultiple.
  const handleSelectSession = (session: OperationsSession) => {
    setSelectedSessionIds([session.session_id]);
    setSessionCache(prev => ({ ...prev, [session.session_id]: session }));
  };

  const handleSelectMultiple = (sessions: OperationsSession[]) => {
    const ids = sessions.map(s => s.session_id);
    setSelectedSessionIds(ids);
    const entries: Record<string, OperationsSession> = {};
    for (const s of sessions) entries[s.session_id] = s;
    setSessionCache(prev => ({ ...prev, ...entries }));
  };

  const handleClearSelection = () => {
    setSelectedSessionIds([]);
    setSessionCache({});
  };

  // Handle view details
  const handleViewDetails = (session: OperationsSession) => {
    setDetailSession(session);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setDetailSession(null);
  };

  // Get session count for a status
  const getStatusCount = (status: string): number => {
    const counts = data?.session_counts;
    if (!counts) return 0;
    const backendCount = (counts as Record<string, number>)[status] ?? 0;
    if (backendCount === 0) {
      return sessions.filter(s => s.status === status).length;
    }
    return backendCount;
  };

  // Status filter chip component
  const StatusChip = ({
    status,
    label,
    count,
    active
  }: {
    status: string | null;
    label: string;
    count?: number;
    active: boolean;
  }) => {
    const statusActiveClass = status ? STATUS_CHIP_ACTIVE[status] : null;

    return (
      <button
        onClick={() => handleStatusFilter(status)}
        // eslint-disable-next-line no-restricted-syntax
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
          active && statusActiveClass
            ? statusActiveClass
            : active
              ? 'border-transparent text-white'
              : 'border-hairline'
        }`}
        style={
          active && statusActiveClass
            ? undefined
            : active
              ? { backgroundColor: getColour('activeText') }
              : {
                  backgroundColor: getColour('secondLayer'),
                  color: getColour('normalText')
                }
        }
      >
        {label}
        {count !== undefined && (
          <span
            // eslint-disable-next-line no-restricted-syntax
            className={`px-1.5 py-0.5 rounded-full text-xs ${active ? 'bg-white/20' : ''}`}
            style={
              !active ? { backgroundColor: getColour('thirdLayer') } : undefined
            }
          >
            {count}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="bg-app flex-1 min-h-0 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 py-2 border-b border-hairline">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <PageHeader
            title={t('operations')}
            caption={t('monitor-and-manage-system-operations')}
          />

          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2 md:mb-0">
            {/* Last updated - hidden on mobile */}
            <div
              className="hidden sm:flex items-center gap-1.5 text-xs"
              style={{ color: getColour('inactiveText') }}
            >
              <Clock className="h-3.5 w-3.5" />
              <span>
                {t('last-updated')}: {lastUpdated}
              </span>
            </div>

            {/* Search input */}
            <div
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-2xl text-xs"
              style={{
                backgroundColor: getColour('secondLayer'),
                border: `1px solid ${getColour('border')}`,
                color: getColour('normalText'),
                width: '220px'
              }}
            >
              <Search
                className="h-3.5 w-3.5 flex-shrink-0"
                style={{ color: getColour('inactiveText') }}
              />
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder={t('search-sessions')}
                className="bg-transparent outline-none w-full text-xs min-w-0"
                style={{ color: getColour('normalText') }}
              />
            </div>

            {/* Time range selector */}
            <div className="relative">
              <button
                onClick={() => setIsTimeRangeOpen(!isTimeRangeOpen)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-2xl text-xs font-medium transition-colors"
                style={{
                  backgroundColor: getColour('secondLayer'),
                  color: getColour('normalText'),
                  border: `1px solid ${getColour('border')}`
                }}
                title={activeTimeRangeLabel}
              >
                <Calendar className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{activeTimeRangeLabel}</span>
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
              {isTimeRangeOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsTimeRangeOpen(false)}
                  />
                  <div
                    className="absolute right-0 top-full mt-1 z-20 py-1 rounded-2xl shadow-lg min-w-[140px]"
                    style={{
                      backgroundColor: getColour('secondLayer'),
                      border: `1px solid ${getColour('border')}`
                    }}
                  >
                    {timeRangeOptions.map(option => (
                      <button
                        key={option.value}
                        onClick={() => handleTimeRangeChange(option.value)}
                        className="w-full px-3 py-1.5 text-left text-xs transition-colors hover:opacity-80"
                        style={{
                          backgroundColor:
                            timeRange === option.value
                              ? getColour('thirdLayer')
                              : 'transparent',
                          color: getColour('normalText')
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                    {timeRange === 'custom' && (
                      <div
                        className="px-3 py-2 flex flex-col gap-1.5 border-t"
                        style={{ borderColor: getColour('border') }}
                      >
                        <label
                          className="text-xs"
                          style={{ color: getColour('inactiveText') }}
                        >
                          {t('from')}
                        </label>
                        <input
                          type="datetime-local"
                          value={dateFrom ?? ''}
                          onChange={e =>
                            handleCustomDateChange('date_from', e.target.value)
                          }
                          className="w-full px-2 py-1 rounded-2xl text-xs"
                          style={{
                            backgroundColor: getColour('thirdLayer'),
                            color: getColour('normalText'),
                            border: `1px solid ${getColour('border')}`
                          }}
                        />
                        <label
                          className="text-xs mt-1"
                          style={{ color: getColour('inactiveText') }}
                        >
                          {t('until')}
                        </label>
                        <input
                          type="datetime-local"
                          value={dateTo ?? ''}
                          onChange={e =>
                            handleCustomDateChange('date_to', e.target.value)
                          }
                          className="w-full px-2 py-1 rounded-2xl text-xs"
                          style={{
                            backgroundColor: getColour('thirdLayer'),
                            color: getColour('normalText'),
                            border: `1px solid ${getColour('border')}`
                          }}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Auto-refresh toggle */}
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              // eslint-disable-next-line no-restricted-syntax
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-2xl text-xs transition ${
                autoRefresh
                  ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                  : ''
              }`}
              style={
                autoRefresh
                  ? undefined
                  : {
                      backgroundColor: getColour('secondLayer'),
                      color: getColour('inactiveText')
                    }
              }
              title={autoRefresh ? t('auto') : t('paused')}
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`}
              />
              <span className="hidden sm:inline">
                {autoRefresh ? t('auto') : t('paused')}
              </span>
            </button>

            {/* Manual refresh */}
            <Button
              variant="secondary"
              size="small"
              icon={<RefreshCw className="h-4 w-4" />}
              onClick={() => refetch()}
              disabled={isFetching}
              title={t('refresh')}
            />

            {/* Retry all stranded sessions */}
            {totalStrandedSessions > 0 && (
              <Button
                label={
                  <span className="hidden sm:inline">{`${t('retry-all')} (${totalStrandedSessions})`}</span>
                }
                variant="primary"
                size="small"
                icon={<RotateCcw className="h-4 w-4" />}
                onClick={() => setIsRetryModalOpen(true)}
                disabled={isLoadingStrandedIds || isRetrying}
                title={`${t('retry-all-sessions')} (${totalStrandedSessions})`}
              />
            )}
          </div>
        </div>

        {/* Status filters */}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <StatusChip
            status={null}
            label={t('all')}
            count={allSessionsCount}
            active={!statusFilter}
          />
          {SESSION_STATUS_OPTIONS.map(status => (
            <StatusChip
              key={status}
              status={status}
              label={t(status)}
              count={getStatusCount(status)}
              active={statusFilter === status}
            />
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 min-h-0 flex flex-col overflow-y-auto lg:overflow-y-hidden py-4 gap-4">
        {/* System Health Cards - Top Row */}
        <div className="flex-shrink-0">
          <SystemHealth
            data={data?.system_health || null}
            isLoading={isLoading}
          />
        </div>

        {/* Bottom Row - Table + Actions Panel */}
        <div className="flex-1 flex flex-col lg:flex-row gap-4 min-h-0">
          {/* Table - Takes most of the space */}
          <div
            className="flex-1 overflow-hidden rounded-2xl min-h-[300px] lg:min-h-0"
            style={{ backgroundColor: getColour('baseLayer') }}
          >
            <LiveActivity
              sessions={filteredSessions}
              // Server-side pagination: pass the API's total_results (not the
              // current page's row count) so DataGridV2 renders pager controls
              // and the user can move beyond page 1. Previously this was
              // filteredSessions.length (= limit, e.g. 25) which yielded
              // totalPages=1 and hid the pager on prod's 2000+ session lists.
              totalItems={data?.total_results ?? filteredSessions.length}
              isLoading={isLoading}
              onSelectSession={handleSelectSession}
              onSelectMultiple={handleSelectMultiple}
              onViewDetails={handleViewDetails}
            />
          </div>

          {/* Contextual Actions Panel - Fixed width on desktop, full width on mobile */}
          <div
            className="flex-shrink-0 w-full lg:w-80 overflow-y-auto rounded-2xl"
            style={{ backgroundColor: getColour('baseLayer') }}
          >
            <ContextualActions
              selectedSessions={selectedSessions}
              onViewDetails={handleViewDetails}
              onClearSelection={handleClearSelection}
            />
          </div>
        </div>
      </div>

      {/* Session Detail Drawer */}
      <SessionDetailDrawer
        session={detailSession}
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
      />

      {/* Retry All Stranded Sessions Modal */}
      <ConfirmationModal
        isOpen={isRetryModalOpen}
        onClose={() => setIsRetryModalOpen(false)}
        onConfirm={handleRetryAllStranded}
        size="small"
        title={t('retry-all-sessions')}
        caption={`${t('retry-all-sessions-confirmation')} (${totalStrandedSessions} ${t('sessions')})`}
        confirmLabel={isRetrying ? t('retrying') : t('retry')}
        cancelLabel={t('cancel')}
        confirmDisabled={isRetrying || !strandedSessionIds.length}
        destructive={false}
        autoFocusConfirm
      />
    </div>
  );
}
